import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * ConsentPage — rendered when Spring Authorization Server needs the user to approve
 * requested OAuth2 scopes before issuing an authorization code.
 *
 * Spring AS redirects here as:
 *   GET /consent?client_id={id}&scope={space-separated scopes}&state={internal state}
 *
 * On approval/denial we POST back to /oauth2/authorize (Spring AS reads client_id + state
 * to look up the original SavedRequest, then applies the consent decision).
 *
 * This follows the Spring Authorization Server reference consent page pattern:
 * https://docs.spring.io/spring-authorization-server/reference/guides/how-to-userinfo.html
 */
export default function ConsentPage() {
  const [searchParams] = useSearchParams();

  // Spring AS injects these on the redirect to /consent
  const clientId = searchParams.get('client_id') ?? '';
  const state    = searchParams.get('state')     ?? '';
  const scopes   = (searchParams.get('scope') ?? '').split(' ').filter(Boolean);

  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(() => new Set(scopes));
  const [loading, setLoading] = useState(false);

  const isValid = clientId && state && scopes.length > 0;

  /** Read XSRF-TOKEN cookie set by Spring CookieCsrfTokenRepository */
  const getCsrfToken = (): string =>
    document.cookie.split(';').map(c => c.trim().split('=')).find(([n]) => n === 'XSRF-TOKEN')?.[1] ?? '';

  /**
   * POST to /oauth2/authorize with client_id, state, and selected scopes.
   * Spring AS completes the authorization code flow using the stored SavedRequest.
   */
  const submitConsent = (approved: boolean) => {
    setLoading(true);
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/oauth2/authorize';

    const add = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };

    add('client_id', clientId);
    add('state', state);
    add('_csrf', getCsrfToken());

    if (approved) {
      // Send each approved scope as a separate 'scope' field
      for (const scope of selectedScopes) {
        add('scope', scope);
      }
    }
    // Deny = submit without any scope values → Spring AS returns access_denied

    document.body.appendChild(form);
    form.submit();
  };

  const toggleScope = (scope: string) => {
    setSelectedScopes(prev => {
      const next = new Set(prev);
      next.has(scope) ? next.delete(scope) : next.add(scope);
      return next;
    });
  };

  const getScopeIcon = (scope: string) => ({ openid:'🔐', profile:'👤', email:'📧', phone:'📱', address:'🏠', offline_access:'🔄' } as Record<string,string>)[scope] ?? '✓';

  const getScopeDescription = (scope: string) => ({
    openid: 'Authenticate with OpenID Connect',
    profile: 'Access your profile information (name, picture)',
    email: 'Access your email address',
    phone: 'Access your phone number',
    address: 'Access your physical address',
    offline_access: 'Access your data when offline (refresh token)',
  } as Record<string,string>)[scope] ?? scope;

  if (!isValid) {
    return (
      <div className="auth-container">
        <div className="auth-header"><div className="auth-logo">ZoAuth</div></div>
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)' }}>
          <p>Invalid or expired authorization request.</p>
          <a href="/login" style={{ color: 'var(--primary)', marginTop: '12px', display: 'block' }}>Return to sign in</a>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-header">
        <div className="auth-logo">ZoAuth</div>
        <div className="auth-subtitle">Authorization Request</div>
      </div>
      <div className="client-info">
        <div className="client-name">{clientId}</div>
      </div>
      <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
        <strong>{clientId}</strong> is requesting permission to access:
      </p>
      <ul className="scope-list">
        {scopes.map((scope) => (
          <li key={scope} className="scope-item" style={{ cursor: 'pointer' }} onClick={() => toggleScope(scope)}>
            <input type="checkbox" checked={selectedScopes.has(scope)} onChange={() => toggleScope(scope)} style={{ marginRight: '8px', cursor: 'pointer' }} />
            <span className="scope-icon">{getScopeIcon(scope)}</span>
            <div>
              <div style={{ fontWeight: 500 }}>{scope}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{getScopeDescription(scope)}</div>
            </div>
          </li>
        ))}
      </ul>
      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button type="button" className="btn btn-secondary" onClick={() => submitConsent(false)} disabled={loading} style={{ flex: 1 }}>Deny</button>
        <button type="button" className="btn btn-primary" onClick={() => submitConsent(true)} disabled={loading || selectedScopes.size === 0} style={{ flex: 1 }}>{loading ? 'Processing…' : 'Allow'}</button>
      </div>
      <div className="footer-links">
        <a href="/privacy" className="footer-link">Privacy Policy</a>
        <span style={{ color: 'var(--text-secondary)' }}>·</span>
        <a href="/terms" className="footer-link">Terms of Service</a>
      </div>
    </div>
  );
}
